/**
 * Menu navigation shared by every screen: the pad's edge-detected D-pad / stick / A / B (`PadNav`, the UI system's
 * menus and the online panels), and `focusStep`, which moves keyboard focus to the nearest control in a direction
 * for screens built from plain buttons (the online panels: they had Tab and the mouse, no pad and no arrows).
 * docs/UI-CONSISTENCY.md.
 */
export type NavAction = 'up' | 'down' | 'left' | 'right' | 'confirm' | 'back';

/** Edge-detected gamepad navigation for menus (D-pad / left stick / A / B). */
export class PadNav {
  private prev = new Set<string>();
  private repeat = 0;

  poll(dt: number): NavAction[] {
    const out: NavAction[] = [];
    if (typeof navigator === 'undefined' || typeof navigator.getGamepads !== 'function') return out;
    let pad: Gamepad | null = null;
    for (const p of navigator.getGamepads()) {
      if (p?.connected) {
        pad = p;
        break;
      }
    }
    if (!pad) {
      this.prev.clear();
      return out;
    }
    const down = new Set<string>();
    const b = (i: number): boolean => !!pad!.buttons[i]?.pressed;
    const ax = pad.axes[0] ?? 0;
    const ay = pad.axes[1] ?? 0;
    if (b(12) || ay < -0.6) down.add('up');
    if (b(13) || ay > 0.6) down.add('down');
    if (b(14) || ax < -0.6) down.add('left');
    if (b(15) || ax > 0.6) down.add('right');
    if (b(0)) down.add('confirm');
    if (b(1)) down.add('back');
    this.repeat -= dt;
    for (const d of down) {
      const fresh = !this.prev.has(d);
      const repeatable = d === 'up' || d === 'down' || d === 'left' || d === 'right';
      if (fresh || (repeatable && this.repeat <= 0)) {
        out.push(d as NavAction);
        if (fresh) this.repeat = 0.38;
        else this.repeat = 0.12;
      }
    }
    this.prev = down;
    return out;
  }
}

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * The index of the box to go to from `from` in direction `dir`: the nearest whose centre lies that way, the
 * distance along the direction weighted less than the drift across it (a row of tabs, then the list under them).
 * -1 when there is none. Pure, for the tests.
 */
export function pickInDirection(boxes: readonly Box[], from: number, dir: 'up' | 'down' | 'left' | 'right'): number {
  const a = boxes[from];
  if (!a) return boxes.length ? 0 : -1;
  const ax = a.x + a.w / 2;
  const ay = a.y + a.h / 2;
  let best = -1;
  let bestScore = Infinity;
  boxes.forEach((b, i) => {
    if (i === from) return;
    const dx = b.x + b.w / 2 - ax;
    const dy = b.y + b.h / 2 - ay;
    const along = dir === 'up' ? -dy : dir === 'down' ? dy : dir === 'left' ? -dx : dx;
    const across = dir === 'up' || dir === 'down' ? Math.abs(dx) : Math.abs(dy);
    if (along <= 1) return;
    const score = along + across * 2;
    if (score < bestScore) {
      bestScore = score;
      best = i;
    }
  });
  return best;
}

/**
 * Move focus inside `root` one step in `dir` (the first control when nothing in it has focus). Returns whether
 * focus moved. Hidden controls are skipped; the new one is scrolled into view.
 */
export function focusStep(root: HTMLElement, dir: 'up' | 'down' | 'left' | 'right'): boolean {
  const els = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((e) => e.getClientRects().length > 0 && getComputedStyle(e).visibility !== 'hidden');
  if (!els.length) return false;
  const boxes = els.map((e) => {
    const r = e.getBoundingClientRect();
    return { x: r.left, y: r.top, w: r.width, h: r.height };
  });
  const cur = els.indexOf(document.activeElement as HTMLElement);
  const next = cur < 0 ? 0 : pickInDirection(boxes, cur, dir);
  if (next < 0) return false;
  // focusVisible: the ring shows for a pad too (the browser only guesses it from a key press)
  els[next]!.focus({ preventScroll: true, focusVisible: true } as FocusOptions);
  els[next]!.scrollIntoView?.({ block: 'nearest' });
  return true;
}
